/** Check-based grader — Mission Control dark styling. */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { CheckAnswer, CheckAnswers, CheckComments, PhaseCriteria, Rag, ReviewCheck } from "./types";
import { RAG_OPTIONS, ragOption } from "./RagOption";
import { deriveRag } from "./deriveRag";

interface RatingPanelProps {
  criteria: PhaseCriteria;
  answers: CheckAnswers;
  checkComments: CheckComments;
  rag: Rag | null;
  note: string;
  onAnswer: (checkId: string, answer: CheckAnswer) => void;
  onCheckComment: (checkId: string, comment: string) => void;
  onOverride: (rag: Rag) => void;
  onNote: (note: string) => void;
  /** Stack check label above answer buttons (better in a narrow side column). */
  compact?: boolean;
  /** Check ids pre-filled as N/A for this cheat sheet variant. */
  autoNaCheckIds?: ReadonlySet<string>;
  autoNaReason?: string;
}

const ANSWER_OPTIONS: { value: CheckAnswer; label: string }[] = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "na", label: "N/A" },
];

function answerTone(value: CheckAnswer, selected: boolean): string {
  if (!selected) {
    return "ring-white/10 text-slate-400 hover:bg-white/[0.04] hover:text-slate-200";
  }
  if (value === "pass") return "bg-emerald-500/15 ring-emerald-500/40 text-emerald-200";
  if (value === "fail") return "bg-rose-500/15 ring-rose-500/40 text-rose-200";
  return "bg-white/[0.06] ring-white/20 text-slate-300";
}

function CheckRow({
  check,
  answer,
  comment,
  onAnswer,
  onCheckComment,
  compact = false,
  autoNa = false,
  autoNaReason,
}: {
  check: ReviewCheck;
  answer: CheckAnswer | undefined;
  comment: string;
  onAnswer: (answer: CheckAnswer) => void;
  onCheckComment: (comment: string) => void;
  compact?: boolean;
  autoNa?: boolean;
  autoNaReason?: string;
}) {
  return (
    <div className={`flex flex-col gap-2 py-3 ${autoNa ? "opacity-80" : ""}`}>
      <div
        className={`flex flex-col gap-2 ${
          compact ? "" : "sm:flex-row sm:items-start sm:justify-between sm:gap-4"
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-slate-200">{check.label}</p>
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ring-1 ${
                check.severity === "hard"
                  ? "text-rose-300 ring-rose-500/30 bg-rose-500/10"
                  : "text-amber-300 ring-amber-500/30 bg-amber-500/10"
              }`}
            >
              {check.severity === "hard" ? "Required" : "Nice to have"}
            </span>
          </div>
          {check.help_text ? (
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">{check.help_text}</p>
          ) : null}
          {autoNa && answer === "na" ? (
            <p className="mt-1 text-[11px] text-slate-500">
              Auto N/A — not applicable for {autoNaReason ?? "this variant"}.
            </p>
          ) : null}
        </div>
        <div className="flex flex-shrink-0 gap-1.5 min-w-[12rem]">
          {ANSWER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onAnswer(opt.value);
                if (opt.value !== "fail") onCheckComment("");
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ring-1 transition-colors ${answerTone(opt.value, answer === opt.value)}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {answer === "fail" ? (
        <div>
          <label className="block text-[11px] text-slate-400 mb-1">
            What went wrong? <span className="text-rose-400">(required)</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => onCheckComment(e.target.value)}
            placeholder="e.g. Policy number on the letter does not match Plannr"
            rows={2}
            className="w-full rounded-lg bg-slate-900/50 px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-500 ring-1 ring-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
          />
        </div>
      ) : null}
    </div>
  );
}

export function RatingPanel({
  criteria,
  answers,
  checkComments,
  rag,
  note,
  onAnswer,
  onCheckComment,
  onOverride,
  onNote,
  compact = false,
  autoNaCheckIds,
  autoNaReason,
}: RatingPanelProps) {
  const derived = deriveRag(criteria, answers);
  const genericChecks = criteria.checks.filter((c) => c.generic);
  const taskChecks = criteria.checks.filter((c) => !c.generic);
  const overridden = rag !== null && derived.rag !== null && rag !== derived.rag;
  const requireNote = rag === "amber" || rag === "red" || overridden;

  return (
    <div className={`space-y-4 ${compact ? "" : "mx-auto max-w-3xl"}`}>
      <div>
        <h3 className="text-base font-medium text-slate-100">{criteria.question}</h3>
        {criteria.intro ? (
          <p className="text-sm text-slate-400 mt-1 leading-relaxed">{criteria.intro}</p>
        ) : null}
      </div>

      {taskChecks.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-400">About this specific task</p>
          <div className="rounded-xl bg-white/[0.02] px-4 ring-1 ring-white/5 divide-y divide-white/5">
            {taskChecks.map((check) => (
              <CheckRow
                key={check.id}
                check={check}
                answer={answers[check.id]}
                comment={checkComments[check.id] ?? ""}
                onAnswer={(a) => onAnswer(check.id, a)}
                onCheckComment={(c) => onCheckComment(check.id, c)}
                compact={compact}
                autoNa={autoNaCheckIds?.has(check.id) ?? false}
                autoNaReason={autoNaReason}
              />
            ))}
          </div>
        </div>
      )}

      {genericChecks.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-400">About any agent run</p>
          <div className="rounded-xl bg-white/[0.02] px-4 ring-1 ring-white/5 divide-y divide-white/5">
            {genericChecks.map((check) => (
              <CheckRow
                key={check.id}
                check={check}
                answer={answers[check.id]}
                comment={checkComments[check.id] ?? ""}
                onAnswer={(a) => onAnswer(check.id, a)}
                onCheckComment={(c) => onCheckComment(check.id, c)}
                compact={compact}
              />
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl bg-white/[0.03] px-4 py-4 ring-1 ring-white/5 space-y-4">
        {derived.rag ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-400">Suggested:</span>
            <span className={`inline-flex items-center gap-1.5 font-medium ${ragOption(derived.rag).text}`}>
              <span className={`w-2 h-2 rounded-full ${ragOption(derived.rag).dot}`} aria-hidden />
              {ragOption(derived.rag).label}
            </span>
          </div>
        ) : (
          <p className="text-sm text-slate-400 flex items-center gap-2">
            <FontAwesomeIcon icon={faCircleInfo} />
            Answer every question above to get a suggested rating.
          </p>
        )}

        <fieldset>
          <legend className="text-xs text-slate-400 mb-2">
            Your rating {derived.rag ? "— change only if you disagree" : ""}
          </legend>
          <div className="flex gap-2">
            {RAG_OPTIONS.map((opt) => {
              const selected = rag === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onOverride(opt.value)}
                  className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium ring-1 transition-all ${
                    selected
                      ? `${opt.ring} ring-2 bg-white/[0.04] ${opt.text}`
                      : "ring-white/10 text-slate-400 hover:bg-white/[0.04]"
                  }`}
                >
                  <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${opt.dot}`} aria-hidden />
                  {opt.label}
                </button>
              );
            })}
          </div>
          {overridden ? (
            <p className="text-xs text-amber-300/90 mt-2">
              You changed the suggested rating — please explain in the note.
            </p>
          ) : null}
        </fieldset>

        <div>
          <label htmlFor="review-note" className="block text-xs text-slate-400 mb-1.5">
            Note {requireNote ? <span className="text-rose-400">(required)</span> : "(optional)"}
          </label>
          <textarea
            id="review-note"
            value={note}
            onChange={(e) => onNote(e.target.value)}
            placeholder="e.g. Scored goals as complete but Plannr showed no goals"
            rows={2}
            className="w-full rounded-xl bg-slate-900/50 px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 ring-1 ring-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
          />
        </div>
      </div>
    </div>
  );
}
